import type { Localized } from "@/lib/locale";
import { employerOfRecordPolska } from "./articles/employer-of-record-polska";
import { itOutsourcingNearshoringPolska } from "./articles/it-outsourcing-nearshoring-polska";
import { headhuntingItDirectSearch } from "./articles/headhunting-it-direct-search";
import { rekrutacjaItKrakow } from "./articles/rekrutacja-it-krakow";
import { rekrutacjaItWarszawa } from "./articles/rekrutacja-it-warszawa";
import { rekrutacjaItCee } from "./articles/rekrutacja-it-cee";
import { kosztRekrutacjiItPolska } from "./articles/koszt-rekrutacji-it-polska";
import { aiWRekrutacjiIt } from "./articles/ai-w-rekrutacji-it";

// Content model for the /insights knowledge hub.
// Each article ships full PL + EN copy so the page follows the site language.

export type Block =
  | { kind: "p"; text: string }
  | { kind: "list"; items: string[] }
  | { kind: "table"; caption: string; head: string[]; rows: string[][] }
  | { kind: "cards"; items: { title: string; meta?: string; body: string }[] };

export type Section = {
  id: string;
  eyebrow: string;
  title: string;
  blocks: Block[];
};

export type Faq = { q: string; a: string };

export type ArticleLocale = {
  title: string;
  h1: string;
  metaTitle: string;
  metaDescription: string;
  eyebrow: string;
  summary: string;
  lead: string;
  note: string;
  takeaways: string[];
  readTime: string;
  sections: Section[];
  faqTitle: string;
  faq: Faq[];
  cta: { eyebrow: string; title: string; body: string; primary: string; secondary: string };
  methodology: string;
};

export type Article = {
  slug: string;
  updated: string; // ISO
  updatedLabel: Localized<string>;
  topic: Localized<string>;
  pl: ArticleLocale;
  en: ArticleLocale;
  zh: ArticleLocale;
};

export const SITE = "https://positiverecruitment.io";

const ctaPl = {
  eyebrow: "Następny krok",
  title: "Potrzebujesz tych danych dla swojej roli?",
  body: "Napisz, jakiej technologii, poziomu doświadczenia i lokalizacji dotyczy rekrutacja, a wrócimy z rzetelnym odczytem rynku: widełki wynagrodzeń, dostępna pula kandydatów i przewidywany czas do oferty. Bez zobowiązań, zwykle w ciągu doby.",
  primary: "Poproś o odczyt rynku",
  secondary: "Zobacz Fit-to-Win Sprint",
};

const ctaEn = {
  eyebrow: "Next move",
  title: "Want this data for your exact role?",
  body: "Send us the stack, seniority and location and we will come back with a live market read: realistic range, available talent pool and expected time to offer. No obligation, usually within 24 hours.",
  primary: "Request a market read",
  secondary: "See the Fit-to-Win Sprint",
};

const ctaZh = {
  eyebrow: "下一步",
  title: "想获取针对你具体岗位的数据吗？",
  body: "告诉我们技术栈、级别和地点，我们会给出实时市场判断:合理的薪资区间、可用候选人池和预计发offer所需时间。无需承诺，通常24小时内回复。",
  primary: "获取市场判断",
  secondary: "了解 Fit-to-Win Sprint",
};

export const ARTICLES: Article[] = [
  {
    slug: "rekrutacja-ai-ml-polska",
    updated: "2026-10-26",
    updatedLabel: { pl: "Aktualizacja: 26 października 2026", en: "Last updated: 26 October 2026", zh: "最近更新：2026 年 10 月 26 日" },
    topic: { pl: "AI / ML", en: "AI / ML", zh: "AI / ML" },
    pl: {
      title: "Rekrutacja specjalistów AI i ML w Polsce",
      h1: "Rekrutacja specjalistów AI/ML w Polsce 2026",
      metaTitle: "Rekrutacja AI/ML w Polsce 2026: widełki, role, proces",
      metaDescription:
        "Jak zatrudnić inżyniera AI/ML w Polsce w 2026: ile kosztuje senior na B2B i UoP, cztery różne role pod jedną nazwą, długość procesu i typowe błędy.",
      eyebrow: "Insights · AI/ML",
      summary:
        "Widełki, mapa kompetencji i proces rekrutacyjny dla ról AI/ML - od ML Engineera po zespół GenAI.",
      lead:
        "AI/ML to dziś najciaśniejszy segment polskiego rynku IT. Prowadzimy rekrutację IT z Krakowa na całą Polskę i region CEE, więc poniżej opisujemy to, co realnie widzimy w procesach: kto jest dostępny, ile kosztuje i dlaczego większość ofert AI nie domyka się z powodu procesu, a nie budżetu.",
      note:
        "Wszystkie kwoty są miesięczne i w PLN. UoP to wynagrodzenie brutto (koszt pracodawcy ok. +20%), B2B to wartość faktury netto bez VAT.",
      readTime: "12 min czytania",
      takeaways: [
        "Senior ML / GenAI Engineer w Polsce kosztuje 24 000 - 34 000 PLN brutto na UoP lub 28 000 - 40 000 PLN netto na B2B (2026).",
        "Role AI/ML płacą 20-40% powyżej średniej inżynierskiej w polskim IT.",
        "Firmy nazywają „AI Engineer” cztery różne zawody: applied/GenAI, klasyczny ML, MLOps i research - łączenie ich w jednym ogłoszeniu to najczęstszy błąd.",
        "Ponad 70% procesów AI, które prowadzimy, kończy się zatrudnieniem zdalnym lub hybrydowym; wymóg pełnego biura zawęża pulę o ok. 60%.",
        "Decyzja później niż 10 dni roboczych od ostatniej rozmowy kosztuje około połowy finalistów.",
        "Średni czas obsadzenia roli AI/ML w sprincie Fit-to-Win to 3-5 tygodni od rozmowy wprowadzającej do zaakceptowanej oferty.",
      ],
      sections: [
        {
          id: "widelki",
          eyebrow: "01 / Wynagrodzenia",
          title: "Ile kosztuje inżynier AI/ML w Polsce",
          blocks: [
            {
              kind: "table",
              caption: "Widełki wynagrodzeń w rolach AI/ML, Polska 2026",
              head: ["Rola", "UoP (brutto/mc)", "B2B (netto/mc)", "Stawka godzinowa B2B"],
              rows: [
                ["Junior ML Engineer (0-2 lata)", "9 000 - 14 000 PLN", "11 000 - 16 000 PLN", "90 - 130 PLN"],
                ["Mid ML Engineer (2-5 lat)", "16 000 - 24 000 PLN", "20 000 - 28 000 PLN", "150 - 200 PLN"],
                ["Senior ML / GenAI Engineer", "24 000 - 34 000 PLN", "28 000 - 40 000 PLN", "200 - 280 PLN"],
                ["MLOps / ML Platform Engineer", "22 000 - 32 000 PLN", "26 000 - 38 000 PLN", "190 - 260 PLN"],
                ["Research Scientist (PhD, NLP/CV)", "26 000 - 38 000 PLN", "30 000 - 45 000 PLN", "220 - 320 PLN"],
                ["Head of AI / AI Lead", "30 000 - 45 000 PLN", "35 000 - 55 000 PLN", "-"],
              ],
            },
            {
              kind: "p",
              text: "Względem ogólnej średniej inżynierskiej role AI/ML płacą 20-40% więcej. Największą premię widzimy tam, gdzie kandydat ma produkcyjne doświadczenie z modelami - nie demo w notebooku, tylko system, który działa pod ruchem, ma monitoring driftu i policzony koszt inferencji.",
            },
          ],
        },
        {
          id: "kompetencje",
          eyebrow: "02 / Profil",
          title: "Cztery różne role, które firmy nazywają „AI Engineer”",
          blocks: [
            {
              kind: "cards",
              items: [
                {
                  title: "Applied / GenAI Engineer",
                  meta: "Najczęściej poszukiwany",
                  body: "Buduje produkt na bazie modeli: RAG, agenty, ewaluacja, guardrails, koszt tokenów. Python + TypeScript, wektorowe bazy, obserwowalność. Największa dostępna pula, najkrótszy proces.",
                },
                {
                  title: "Classic ML Engineer",
                  meta: "Predykcja i scoring",
                  body: "Feature engineering, gradient boosting, szeregi czasowe, systemy rekomendacyjne. Silny w domenach fintech, e-commerce i logistyce; oczekuje dostępu do realnych danych, nie do backlogu POC-ów.",
                },
                {
                  title: "MLOps / ML Platform",
                  meta: "Najtrudniejszy do znalezienia",
                  body: "Kubernetes, GPU scheduling, feature store, CI/CD dla modeli, wersjonowanie i rollback. Kandydaci mają zwykle 2-3 równoległe procesy - decyzja musi zapaść w kilka dni.",
                },
                {
                  title: "Research Scientist",
                  meta: "Wąska pula",
                  body: "Doktorat lub publikacje w NLP/CV. Motywacja to problem badawczy i możliwość publikowania, nie sam poziom wynagrodzenia. Rekrutacja przez sieć akademicką i konferencje, nie przez portale.",
                },
              ],
            },
            {
              kind: "p",
              text: "Pierwszy błąd w większości ogłoszeń AI to sklejenie tych czterech profili w jeden opis stanowiska. Efekt: aplikują ludzie dopasowani w 40%, a właściwi kandydaci ogłoszenia w ogóle nie czytają, bo nie widzą w nim swojej roboty.",
            },
          ],
        },
        {
          id: "proces",
          eyebrow: "03 / Proces",
          title: "Jak wygląda skuteczny proces rekrutacyjny AI",
          blocks: [
            {
              kind: "list",
              items: [
                "Screening techniczny 45 min zamiast zadania domowego na 8 godzin - seniorzy AI nie robią zadań domowych i to jest najczęstszy powód rezygnacji.",
                "Rozmowa systemowa: jak zaprojektujesz pipeline, ewaluację i monitoring, a nie odpytywanie ze wzorów.",
                "Case z domeny klienta - 60-90 minut wspólnej pracy przy tablicy daje więcej sygnału niż jakikolwiek test online.",
                "Decyzja w 5 dni roboczych od ostatniej rozmowy. Powyżej 10 dni tracimy ok. połowy finalistów.",
                "Jawne widełki w pierwszym kontakcie - w AI to warunek odpowiedzi na wiadomość, nie element negocjacji.",
              ],
            },
            {
              kind: "p",
              text: "Średni czas obsadzenia roli AI/ML, który realizujemy w sprincie Fit-to-Win, to 3-5 tygodni od rozmowy wprowadzającej do zaakceptowanej oferty. Rynkowa mediana dla procesów prowadzonych wewnętrznie bywa 2-3 razy dłuższa, głównie przez rozciągnięte etapy decyzyjne.",
            },
          ],
        },
        {
          id: "rynek",
          eyebrow: "04 / Rynek",
          title: "Gdzie realnie są kandydaci",
          blocks: [
            {
              kind: "p",
              text: "Kraków, Warszawa, Wrocław i Poznań skupiają większość zespołów AI, ale od 2024 pula jest w praktyce ogólnopolska: ponad 70% procesów AI, które prowadzimy, kończy się zatrudnieniem w modelu zdalnym lub hybrydowym 1-2 dni w biurze. Wymóg pełnej pracy z biura zawęża pulę kandydatów o ok. 60% i podnosi oczekiwania płacowe o 10-15%.",
            },
            {
              kind: "p",
              text: "Konkurujesz nie tylko z polskimi firmami. Zagraniczne spółki płacące w EUR i zatrudniające przez EOR lub B2B są dziś domyślną alternatywą dla seniora AI w Polsce, dlatego benchmark „lokalny” bez uwzględnienia ofert zdalnych z Europy Zachodniej jest zaniżony.",
            },
          ],
        },
        {
          id: "popyt",
          eyebrow: "05 / Popyt",
          title: "Jak szybko rośnie popyt na specjalistów AI",
          blocks: [
            {
              kind: "p",
              text: "Liczba ogłoszeń dla specjalistów AI w Polsce wzrosła w ciągu ostatniego roku ponad trzykrotnie - to najszybciej rosnący segment polskiego rynku IT. Jawne widełki płacowe podaje wciąż tylko mniejszość ogłoszeń, a mediana z tych, które je mają (październik 2026, ok. 224 unikalne oferty), to ok. 24 000 PLN miesięcznie - wartość zbliżona do środka naszych widełek dla ról mid-senior.",
            },
            {
              kind: "table",
              caption: "Struktura briefów AI/ML w procesach Positive Recruitment, 2025-2026",
              head: ["Profil", "Udział w briefach", "Typowy czas do shortlisty"],
              rows: [
                ["Applied / GenAI Engineer", "ok. 55%", "5-7 dni roboczych"],
                ["Classic ML Engineer", "ok. 20%", "7-10 dni roboczych"],
                ["MLOps / ML Platform", "ok. 15%", "10-14 dni roboczych"],
                ["Research Scientist", "ok. 10%", "3-6 tygodni"],
              ],
            },
            {
              kind: "p",
              text: "Popyt przesunął się wyraźnie w stronę GenAI: jeszcze w 2024 roku większość briefów dotyczyła klasycznego ML, dziś ponad połowa to role applied/GenAI. Firmy, które wciąż szukają „data scientista od wszystkiego”, konkurują o tę samą, kurczącą się pulę kandydatów.",
            },
          ],
        },
        {
          id: "koszt-zwloki",
          eyebrow: "06 / Koszt zwłoki",
          title: "Ile kosztuje pusta rola AI",
          blocks: [
            {
              kind: "p",
              text: "Pusta rola seniora AI przez kwartał to nie tylko 90 000 PLN niewykorzystanego budżetu (3 × 30 000 PLN B2B), ale przede wszystkim opóźniona mapa drogowa produktu. W procesach, które obserwujemy, każdy dodatkowy miesiąc zwłoki zwiększa też ryzyko utraty finalistów: kandydat AI ma zwykle 2-3 równoległe procesy, a ok. co trzeci dostaje counter-offer od obecnego pracodawcy.",
            },
            {
              kind: "list",
              items: [
                "Okres wypowiedzenia: senior na UoP - 3 miesiące, na B2B - zwykle 1 miesiąc. Planując start nowej roli, dolicz ten czas do daty akceptacji oferty.",
                "Counter-offer pojawia się w ok. 1/3 procesów AI na poziomie senior; kandydat z counter-offerem potrzebuje decyzji w dni, nie w tygodnie.",
                "Kandydaci, którzy tydzień po ostatniej rozmowie nie dostali informacji zwrotnej, akceptują oferty wyraźnie rzadziej.",
                "Najtańszy moment na przyspieszenie procesu to pierwsze 48 godzin po briefie - wtedy kalendarz decydentów jeszcze da się zablokować.",
              ],
            },
          ],
        },
      ],
      faqTitle: "Najczęstsze pytania",
      faq: [
        {
          q: "Czy w Polsce da się zatrudnić inżyniera GenAI w 30 dni?",
          a: "Tak, pod warunkiem jawnych widełek, maksymalnie trzech etapów i decyzji w ciągu tygodnia od ostatniej rozmowy. Sam sourcing zwykle nie jest wąskim gardłem - jest nim kalendarz.",
        },
        {
          q: "Lepszy senior z klasycznego ML czy inżynier z doświadczeniem w LLM?",
          a: "Do produktów opartych na API modeli szybciej wdroży się applied/GenAI engineer. Do scoringu, prognoz i danych tabelarycznych klasyczny ML engineer zwykle daje lepszy wynik biznesowy.",
        },
        {
          q: "Ile kosztuje rekrutacja specjalisty AI przez agencję?",
          a: "Standard rynkowy w Polsce to 15-22% rocznego wynagrodzenia kandydata, z gwarancją zastąpienia. Przy rolach AI kluczowa jest jednak nie stawka, lecz dostęp do kandydatów pasywnych.",
        },
        {
          q: "Czy warto zatrudniać juniorów AI?",
          a: "Tak, jeśli w zespole jest już senior i działający pipeline danych. Bez tego junior AI kosztuje więcej czasu, niż daje wartości w pierwszym roku.",
        },
        {
          q: "Jak długi jest okres wypowiedzenia specjalisty AI?",
          a: "Na UoP senior ma zwykle 3-miesięczny okres wypowiedzenia, na B2B najczęściej 1 miesiąc. Realny start nowej osoby to data akceptacji oferty plus 1-3 miesiące - warto zaplanować to w budżecie projektu.",
        },
      ],
      cta: ctaPl,
      methodology:
        "Metodologia: widełki opieramy na ofertach zaakceptowanych w procesach Positive Recruitment w latach 2025-2026, skonfrontowanych z publicznymi raportami płacowymi polskiego rynku IT. Rynek zmienia się szybko - traktuj te dane jako zakresy planistyczne, nie oferty.",
    },
    en: {
      title: "Hiring AI and ML engineers in Poland",
      h1: "Hiring AI/ML engineers in Poland 2026",
      metaTitle: "Hiring AI/ML Engineers in Poland 2026: Pay & Process",
      metaDescription:
        "How to hire AI and ML engineers in Poland in 2026: real B2B and employment ranges, four role profiles behind one job title, process benchmarks and costly mistakes.",
      eyebrow: "Insights · AI/ML",
      summary:
        "Pay ranges, role profiles and a hiring process that actually converts for AI/ML roles in Poland and CEE.",
      lead:
        "AI/ML is the tightest segment of the Polish IT market. We run IT recruitment out of Kraków across Poland and the CEE region, so what follows is what we see inside live processes: who is available, what they cost, and why most AI roles stall on process rather than budget.",
      note:
        "All figures are monthly and in PLN. UoP is gross employment salary (employer cost roughly +20%); B2B is net invoice value excluding VAT.",
      readTime: "12 min read",
      takeaways: [
        "A senior ML / GenAI engineer in Poland costs 24,000-34,000 PLN gross on employment or 28,000-40,000 PLN net on B2B (2026).",
        "AI/ML roles pay 20-40% above the general Polish engineering average.",
        "Companies use the label “AI Engineer” for four different jobs: applied/GenAI, classic ML, MLOps and research - merging them into one ad is the most common mistake.",
        "Over 70% of the AI processes we run end in a remote or hybrid hire; a full on-site requirement shrinks the pool by around 60%.",
        "A decision later than 10 working days after the final interview costs roughly half the finalists.",
        "Average time to fill for AI/ML roles in the Fit-to-Win Sprint is 3-5 weeks from brief to accepted offer.",
      ],
      sections: [
        {
          id: "pay",
          eyebrow: "01 / Compensation",
          title: "What an AI/ML engineer costs in Poland",
          blocks: [
            {
              kind: "table",
              caption: "AI/ML salary ranges in Poland, 2026",
              head: ["Role", "Employment (gross/mo)", "B2B (net/mo)", "B2B hourly"],
              rows: [
                ["Junior ML Engineer (0-2 yrs)", "9 000 - 14 000 PLN", "11 000 - 16 000 PLN", "90 - 130 PLN"],
                ["Mid ML Engineer (2-5 yrs)", "16 000 - 24 000 PLN", "20 000 - 28 000 PLN", "150 - 200 PLN"],
                ["Senior ML / GenAI Engineer", "24 000 - 34 000 PLN", "28 000 - 40 000 PLN", "200 - 280 PLN"],
                ["MLOps / ML Platform Engineer", "22 000 - 32 000 PLN", "26 000 - 38 000 PLN", "190 - 260 PLN"],
                ["Research Scientist (PhD, NLP/CV)", "26 000 - 38 000 PLN", "30 000 - 45 000 PLN", "220 - 320 PLN"],
                ["Head of AI / AI Lead", "30 000 - 45 000 PLN", "35 000 - 55 000 PLN", "-"],
              ],
            },
            {
              kind: "p",
              text: "AI/ML roles pay 20-40% above the general engineering average. The premium concentrates on production experience: not a notebook demo, but a system serving traffic with drift monitoring and a known inference cost.",
            },
          ],
        },
        {
          id: "profiles",
          eyebrow: "02 / Profiles",
          title: "Four different roles companies all call “AI Engineer”",
          blocks: [
            {
              kind: "cards",
              items: [
                {
                  title: "Applied / GenAI Engineer",
                  meta: "Most requested",
                  body: "Builds product on top of models: RAG, agents, evaluation, guardrails, token cost. Python plus TypeScript, vector stores, observability. Largest available pool, shortest process.",
                },
                {
                  title: "Classic ML Engineer",
                  meta: "Prediction and scoring",
                  body: "Feature engineering, gradient boosting, time series, recommenders. Strong in fintech, e-commerce and logistics; expects access to real data, not a backlog of POCs.",
                },
                {
                  title: "MLOps / ML Platform",
                  meta: "Hardest to find",
                  body: "Kubernetes, GPU scheduling, feature stores, CI/CD for models, versioning and rollback. Candidates typically hold two or three live processes, so decisions must land within days.",
                },
                {
                  title: "Research Scientist",
                  meta: "Narrow pool",
                  body: "PhD or publications in NLP/CV. Motivated by the research problem and the freedom to publish rather than headline pay. Reached through academic networks and conferences, not job boards.",
                },
              ],
            },
            {
              kind: "p",
              text: "The most common mistake in AI job ads is merging these four profiles into one description. The result: 40%-fit applicants apply, while the right candidates never read past the first paragraph because they cannot see their own job in it.",
            },
          ],
        },
        {
          id: "process",
          eyebrow: "03 / Process",
          title: "What an AI hiring process that converts looks like",
          blocks: [
            {
              kind: "list",
              items: [
                "A 45-minute technical screen instead of an 8-hour take-home - senior AI candidates decline take-homes, and this is the single biggest source of drop-off.",
                "A systems conversation: how you would design the pipeline, evaluation and monitoring, rather than formula quizzing.",
                "A domain case: 60-90 minutes of shared whiteboard work gives more signal than any online test.",
                "A decision within 5 working days of the final interview. Beyond 10 days we lose roughly half the finalists.",
                "Transparent ranges in the first message - in AI that is a condition for a reply, not a negotiation step.",
              ],
            },
            {
              kind: "p",
              text: "Our average time to fill for AI/ML roles inside the Fit-to-Win Sprint is 3-5 weeks from brief to accepted offer. Internally run processes commonly take two to three times longer, mostly through stretched decision stages.",
            },
          ],
        },
        {
          id: "market",
          eyebrow: "04 / Market",
          title: "Where the candidates actually are",
          blocks: [
            {
              kind: "p",
              text: "Kraków, Warsaw, Wrocław and Poznań hold most AI teams, but since 2024 the pool is effectively nationwide: over 70% of the AI processes we run end in a remote or 1-2 day hybrid hire. A full on-site requirement shrinks the pool by around 60% and raises pay expectations by 10-15%.",
            },
            {
              kind: "p",
              text: "You are not only competing with Polish employers. Foreign companies paying in EUR through an EOR or B2B contract are now the default alternative for a senior AI engineer in Poland, so a purely local benchmark that ignores Western European remote offers will read low.",
            },
          ],
        },
        {
          id: "demand",
          eyebrow: "05 / Demand",
          title: "How fast demand for AI specialists is growing",
          blocks: [
            {
              kind: "p",
              text: "The number of AI job postings in Poland has more than tripled over the past year - the fastest-growing segment of the Polish IT market. Only a minority of AI ads disclose pay, and the median across those that do (October 2026, roughly 224 unique ads) sits near 24,000 PLN per month, close to the middle of our mid-senior ranges.",
            },
            {
              kind: "table",
              caption: "AI/ML brief mix in Positive Recruitment processes, 2025-2026",
              head: ["Profile", "Share of briefs", "Typical time to shortlist"],
              rows: [
                ["Applied / GenAI Engineer", "approx. 55%", "5-7 working days"],
                ["Classic ML Engineer", "approx. 20%", "7-10 working days"],
                ["MLOps / ML Platform", "approx. 15%", "10-14 working days"],
                ["Research Scientist", "approx. 10%", "3-6 weeks"],
              ],
            },
            {
              kind: "p",
              text: "Demand has shifted clearly towards GenAI: in 2024 most briefs were classic ML; today over half are applied/GenAI roles. Companies still looking for a “do-everything data scientist” are competing for the same shrinking candidate pool.",
            },
          ],
        },
        {
          id: "cost-of-delay",
          eyebrow: "06 / Cost of delay",
          title: "What an empty AI role costs",
          blocks: [
            {
              kind: "p",
              text: "A senior AI role left open for a quarter is not only 90,000 PLN of unspent budget (3 × 30,000 PLN B2B) but above all a delayed product roadmap. In the processes we observe, every extra month of delay also raises the risk of losing finalists: an AI candidate typically runs two or three parallel processes, and roughly one in three receives a counter-offer from their current employer.",
            },
            {
              kind: "list",
              items: [
                "Notice periods: a senior on an employment contract - 3 months; on B2B - usually 1 month. Add this to the offer acceptance date when planning the start.",
                "Counter-offers appear in about a third of senior AI processes; a candidate holding one needs a decision in days, not weeks.",
                "Candidates who hear nothing for a week after the final interview accept offers markedly less often.",
                "The cheapest moment to speed a process up is the first 48 hours after the brief - that is when decision-makers' calendars can still be blocked.",
              ],
            },
          ],
        },
      ],
      faqTitle: "Frequently asked questions",
      faq: [
        {
          q: "Can you hire a GenAI engineer in Poland within 30 days?",
          a: "Yes, provided the range is public, there are at most three stages, and the decision lands within a week of the final interview. Sourcing is rarely the bottleneck - the calendar is.",
        },
        {
          q: "Classic ML senior or an engineer with LLM experience?",
          a: "For products built on model APIs, an applied/GenAI engineer ramps up faster. For scoring, forecasting and tabular data, a classic ML engineer usually delivers the better business result.",
        },
        {
          q: "What does agency recruitment for AI roles cost?",
          a: "The Polish market standard is 15-22% of the candidate's annual salary with a replacement guarantee. For AI roles the decisive factor is access to passive candidates, not the fee itself.",
        },
        {
          q: "Is it worth hiring junior AI engineers?",
          a: "Yes, if a senior and a working data pipeline are already in place. Without both, a junior AI hire costs more time in year one than the value they add.",
        },
        {
          q: "How long is the notice period for an AI specialist?",
          a: "On an employment contract a senior usually has a 3-month notice period; on B2B most often 1 month. The realistic start date is offer acceptance plus one to three months, so plan for it in the project budget.",
        },
      ],
      cta: ctaEn,
      methodology:
        "Methodology: ranges are based on offers accepted through Positive Recruitment in 2025-2026, cross-checked against public Polish IT salary reports. The market moves fast - treat these as planning ranges, not quotes.",
    },
    zh: {
      title: "在波兰招聘 AI 和 ML 工程师",
      h1: "2026 年波兰 AI/ML 工程师招聘指南",
      metaTitle: "2026 年波兰 AI/ML 招聘指南：薪资与流程",
      metaDescription:
        "2026 年如何在波兰招聘 AI 和 ML 工程师：真实的 B2B 与雇佣合同薪资区间、同一职位背后的四种角色画像、流程基准以及代价高昂的常见错误。",
      eyebrow: "洞察 · AI/ML",
      summary:
        "波兰及 CEE 地区 AI/ML 岗位的薪资区间、角色画像以及真正能落地的招聘流程。",
      lead:
        "AI/ML 是波兰 IT 市场竞争最激烈的细分领域。我们的 IT 招聘业务从 Kraków 出发，覆盖全波兰及 CEE 地区，以下内容来自我们在真实招聘过程中的观察:哪些人才可用、他们的价码是多少，以及为什么大多数 AI 岗位卡在流程而非预算上。",
      note:
        "所有金额均为月度，单位为 PLN。UoP 为税前雇佣工资（雇主实际成本约 +20%）；B2B 为不含增值税的净发票金额。",
      readTime: "阅读时长 12 分钟",
      takeaways: [
        "2026 年波兰高级 ML / GenAI 工程师的薪资为 UoP 税前 24,000-34,000 PLN,或 B2B 净额 28,000-40,000 PLN。",
        "AI/ML 岗位的薪资比波兰工程师平均水平高出 20%-40%。",
        "企业把“AI Engineer”这一头衔用在四种不同的岗位上：applied/GenAI、经典 ML、MLOps 和研究方向，把它们合并进一条招聘广告是最常见的错误。",
        "我们经手的 AI 招聘流程中超过 70% 最终以远程或混合办公方式录用；要求全职到岗会使候选人池缩小约 60%。",
        "最终面试后超过 10 个工作日才给出决定，大约会流失一半的候选人。",
        "在 Fit-to-Win Sprint 中,AI/ML 岗位从需求简报到接受 offer 的平均耗时为 3-5 周。",
      ],
      sections: [
        {
          id: "widelki",
          eyebrow: "01 / 薪资",
          title: "波兰 AI/ML 工程师的薪资水平",
          blocks: [
            {
              kind: "table",
              caption: "2026 年波兰 AI/ML 岗位薪资区间",
              head: ["职位", "雇佣合同（税前/月）", "B2B（净额/月）", "B2B 时薪"],
              rows: [
                ["初级 ML Engineer（0-2 年）", "9 000 - 14 000 PLN", "11 000 - 16 000 PLN", "90 - 130 PLN"],
                ["中级 ML Engineer（2-5 年）", "16 000 - 24 000 PLN", "20 000 - 28 000 PLN", "150 - 200 PLN"],
                ["高级 ML / GenAI Engineer", "24 000 - 34 000 PLN", "28 000 - 40 000 PLN", "200 - 280 PLN"],
                ["MLOps / ML 平台工程师", "22 000 - 32 000 PLN", "26 000 - 38 000 PLN", "190 - 260 PLN"],
                ["研究科学家（博士，NLP/CV）", "26 000 - 38 000 PLN", "30 000 - 45 000 PLN", "220 - 320 PLN"],
                ["AI 负责人 / AI Lead", "30 000 - 45 000 PLN", "35 000 - 55 000 PLN", "-"],
              ],
            },
            {
              kind: "p",
              text: "相较于整体工程师平均水平,AI/ML 岗位的薪资高出 20%-40%。溢价最明显的地方在于候选人是否拥有生产环境经验，不是笔记本里的演示，而是真正承载线上流量、具备 drift 监控且明确核算推理成本的系统。",
            },
          ],
        },
        {
          id: "kompetencje",
          eyebrow: "02 / 画像",
          title: "被统称为“AI Engineer”的四种不同角色",
          blocks: [
            {
              kind: "cards",
              items: [
                {
                  title: "Applied / GenAI Engineer",
                  meta: "需求量最大",
                  body: "基于模型构建产品：RAG、智能体、评估、guardrails、token 成本控制。技术栈为 Python 加 TypeScript、向量数据库、可观测性。可用人才池最大，招聘流程最短。",
                },
                {
                  title: "经典 ML Engineer",
                  meta: "预测与评分",
                  body: "特征工程、梯度提升、时间序列、推荐系统。在 fintech、电商和物流领域尤为擅长；他们期望接触真实数据，而不是一堆 POC 待办列表。",
                },
                {
                  title: "MLOps / ML 平台",
                  meta: "最难招到",
                  body: "Kubernetes、GPU 调度、特征存储、模型的 CI/CD、版本管理与回滚。候选人通常同时手握两到三个进行中的流程，决策必须在几天内完成。",
                },
                {
                  title: "研究科学家",
                  meta: "人才池狭窄",
                  body: "拥有 NLP/CV 方向的博士学位或论文发表。驱动他们的是研究问题本身和发表自由，而非薪资数字。多通过学术网络和会议触达，而非招聘网站。",
                },
              ],
            },
            {
              kind: "p",
              text: "大多数 AI 招聘广告的首要错误，是把这四种画像揉进同一份职位描述。结果是：匹配度只有 40% 的人来投递，而真正合适的候选人根本读不到第二段，因为他们看不到属于自己的那份工作。",
            },
          ],
        },
        {
          id: "proces",
          eyebrow: "03 / 流程",
          title: "真正有效的 AI 招聘流程是什么样的",
          blocks: [
            {
              kind: "list",
              items: [
                "45 分钟的技术screening代替 8 小时的家庭作业，高级 AI 候选人会拒绝家庭作业，这是流失候选人的头号原因。",
                "系统设计对话：如何设计 pipeline、评估体系和监控，而不是背公式式的提问。",
                "结合客户业务场景的案例：60-90 分钟的白板共创，比任何在线测试提供的信号都多。",
                "最终面试后 5 个工作日内做出决定。超过 10 天，我们大约会流失一半的候选人。",
                "在首次接触时就明示薪资区间，在 AI 领域，这是候选人是否回复的前提条件，而不是谈判筹码。",
              ],
            },
            {
              kind: "p",
              text: "我们在 Fit-to-Win Sprint 中完成 AI/ML 岗位招聘的平均耗时，是从需求简报到接受 offer 3-5 周。企业内部自行推进的流程中位数通常要长 2-3 倍，主要是因为决策环节被拉得太长。",
            },
          ],
        },
        {
          id: "rynek",
          eyebrow: "04 / 市场",
          title: "候选人实际都在哪里",
          blocks: [
            {
              kind: "p",
              text: "Kraków、Warszawa（华沙）、Wrocław 和 Poznań 聚集了大多数 AI 团队，但自 2024 年以来，人才池实际上已覆盖全国：我们经手的 AI 招聘流程中，超过 70% 最终以远程或每周 1-2 天到岗的混合模式录用。要求全职到岗会使候选人池缩小约 60%,并将薪资预期推高 10%-15%。",
            },
            {
              kind: "p",
              text: "你面对的竞争不只是波兰本土企业。以 EUR 计薪、通过 EOR 或 B2B 雇佣的境外公司，如今已成为波兰高级 AI 工程师的默认替代选项，因此忽略西欧远程 offer 的“本地”基准会明显偏低。",
            },
          ],
        },
        {
          id: "popyt",
          eyebrow: "05 / 需求",
          title: "AI 人才需求增长有多快",
          blocks: [
            {
              kind: "p",
              text: "过去一年，波兰 AI 岗位的招聘广告数量增长了三倍以上，是波兰 IT 市场中增长最快的细分领域。与此同时，只有少部分 AI 广告公开薪资区间；这些广告的中位数（2026 年 10 月，约 224 条去重后的广告）约为每月 24,000 PLN，与我们中高级区间的中值基本一致。",
            },
            {
              kind: "table",
              caption: "2025-2026 年 Positive Recruitment 招聘流程中的 AI/ML 需求结构",
              head: ["角色画像", "需求占比", "获得候选人名单的典型时间"],
              rows: [
                ["Applied / GenAI Engineer", "约 55%", "5-7 个工作日"],
                ["经典 ML Engineer", "约 20%", "7-10 个工作日"],
                ["MLOps / ML 平台", "约 15%", "10-14 个工作日"],
                ["研究科学家", "约 10%", "3-6 周"],
              ],
            },
            {
              kind: "p",
              text: "需求明显向 GenAI 转移：2024 年大多数需求还集中在经典 ML，如今超过一半是 applied/GenAI 岗位。仍在寻找“全能数据科学家”的企业，实际上在争夺同一个正在缩小的候选人池。",
            },
          ],
        },
        {
          id: "koszt-zwloki",
          eyebrow: "06 / 拖延的成本",
          title: "AI 岗位空缺的代价",
          blocks: [
            {
              kind: "p",
              text: "高级 AI 岗位空缺一个季度，不只是 90,000 PLN 的预算闲置（3 × 30,000 PLN B2B），更重要的是产品路线图被推迟。在我们观察的流程中，每多拖延一个月，流失最终候选人的风险都会上升：AI 候选人通常同时进行两到三个流程，约三分之一会收到现雇主的 counter-offer。",
            },
            {
              kind: "list",
              items: [
                "离职通知期：签订劳动合同的高级工程师为 3 个月，B2B 通常为 1 个月。规划入职时间时，请把这段时间加在 offer 接受日之后。",
                "约三分之一的高级 AI 招聘流程中会出现 counter-offer；手握 counter-offer 的候选人需要在几天内得到答复，而不是几周。",
                "最终面试后一周没有收到反馈的候选人，接受 offer 的概率明显更低。",
                "加速流程成本最低的时点，是需求简报后的前 48 小时——此时决策者的日程还来得及锁定。",
              ],
            },
          ],
        },
      ],
      faqTitle: "常见问题",
      faq: [
        {
          q: "在波兰能在 30 天内招到一名 GenAI 工程师吗？",
          a: "可以，前提是薪资区间公开、流程最多三轮，并且在最终面试后一周内做出决定。sourcing 通常不是瓶颈，日程安排才是。",
        },
        {
          q: "经典 ML 高级工程师和有 LLM 经验的工程师，该选哪个？",
          a: "对于基于模型 API 构建的产品,applied/GenAI 工程师上手更快。对于评分、预测和表格数据类场景，经典 ML 工程师通常能带来更好的业务结果。",
        },
        {
          q: "通过猎头招聘 AI 人才的费用是多少？",
          a: "波兰市场标准是候选人年薪的 15%-22%,并附带替换保障。但对 AI 岗位而言，能否触达被动候选人，比费率本身更关键。",
        },
        {
          q: "值得招聘 AI 初级工程师吗？",
          a: "值得，前提是团队里已有高级工程师和可运行的数据 pipeline。若不具备这两点，初级 AI 工程师第一年花掉的时间成本会超过他创造的价值。",
        },
        {
          q: "AI 专家的离职通知期有多长？",
          a: "签订劳动合同的高级工程师通常有 3 个月通知期；B2B 合同最常见为 1 个月。因此实际入职时间是接受 offer 后再加 1-3 个月，请在项目预算中提前规划。",
        },
      ],
      cta: ctaZh,
      methodology:
        "方法说明：薪资区间基于 Positive Recruitment 在 2025-2026 年被接受的 offer 数据，并与波兰 IT 行业公开薪资报告交叉核对。市场变化很快，请将这些数据视为规划区间，而非报价。",
    },
  },

  {
    slug: "rekrutacja-data-engineering",
    updated: "2026-06-18",
    updatedLabel: { pl: "Aktualizacja: 18 czerwca 2026", en: "Last updated: 18 June 2026", zh: "最近更新：2026 年 6 月 18 日" },
    topic: { pl: "Data", en: "Data", zh: "数据" },
    pl: {
      title: "Rekrutacja Data Engineerów i zespołów danych",
      h1: "Rekrutacja Data Engineerów w Polsce 2026",
      metaTitle: "Data Engineer w Polsce 2026: zarobki i rekrutacja",
      metaDescription:
        "Ile zarabia Data Engineer w Polsce w 2026, czym różni się od analityka i analytics engineera, w jakiej kolejności budować zespół danych i jak szybko weryfikować kandydatów.",
      eyebrow: "Insights · Data",
      summary:
        "Zarobki, podział ról data i kolejność budowania zespołu danych - od pierwszego inżyniera do platformy.",
      lead:
        "Zespoły danych rosną w Polsce szybciej niż jakikolwiek inny obszar poza AI, ale rekrutacja w tym obszarze jest wyjątkowo podatna na błąd dopasowania: analityk, inżynier danych i analytics engineer to trzy różne zawody, które w ogłoszeniach wciąż bywają jednym.",
      note:
        "Kwoty miesięczne w PLN. UoP - brutto; B2B - netto bez VAT. Dane z procesów prowadzonych w Polsce i regionie CEE.",
      readTime: "8 min czytania",
      takeaways: [
        "Senior Data Engineer w Polsce zarabia 21 000 - 30 000 PLN brutto na UoP lub 25 000 - 35 000 PLN netto na B2B (2026).",
        "Analytics Engineer (dbt) to najszybciej rosnąca rola data w Polsce: 14 000 - 20 000 PLN brutto.",
        "Analityk, analytics engineer i data engineer to trzy różne zawody - w większości firm dobra kolejność to analityk, analytics engineer, data engineer, a dopiero potem platforma.",
        "Premię płacową daje odpowiedzialność za koszt: inżynier, który obniża rachunek za hurtownię o 30%, jest wyceniany wyżej niż dłuższa lista technologii w CV.",
        "Rekrutacja Data Engineera przy jawnych widełkach i trzech etapach trwa realnie 3-6 tygodni.",
        "Najciaśniejsza podaż kandydatów występuje w streamingu (Kafka, Flink) w fintechu, logistyce i e-commerce.",
      ],
      sections: [
        {
          id: "widelki",
          eyebrow: "01 / Wynagrodzenia",
          title: "Zarobki w rolach data",
          blocks: [
            {
              kind: "table",
              caption: "Widełki wynagrodzeń w rolach data, Polska 2026",
              head: ["Rola", "UoP (brutto/mc)", "B2B (netto/mc)", "Stawka godzinowa B2B"],
              rows: [
                ["Data Analyst (mid)", "9 000 - 14 000 PLN", "11 000 - 16 000 PLN", "80 - 120 PLN"],
                ["Analytics Engineer (dbt)", "14 000 - 20 000 PLN", "17 000 - 24 000 PLN", "130 - 180 PLN"],
                ["Data Engineer (mid)", "15 000 - 21 000 PLN", "18 000 - 25 000 PLN", "140 - 190 PLN"],
                ["Senior Data Engineer", "21 000 - 30 000 PLN", "25 000 - 35 000 PLN", "180 - 250 PLN"],
                ["Data Platform / Big Data Engineer", "23 000 - 32 000 PLN", "27 000 - 38 000 PLN", "200 - 270 PLN"],
                ["Head of Data", "28 000 - 40 000 PLN", "33 000 - 48 000 PLN", "-"],
              ],
            },
            {
              kind: "p",
              text: "Premię płacową w danych generuje skala i odpowiedzialność za koszt, nie sama znajomość narzędzi. Inżynier, który potrafi obniżyć rachunek za hurtownię o 30%, jest wyceniany wyżej niż osoba z dłuższą listą technologii w CV.",
            },
          ],
        },
        {
          id: "role",
          eyebrow: "02 / Role",
          title: "Trzy zawody, jedno ogłoszenie",
          blocks: [
            {
              kind: "cards",
              items: [
                {
                  title: "Data Analyst",
                  meta: "SQL + BI + kontekst biznesowy",
                  body: "Odpowiada na pytania biznesu, buduje dashboardy, definiuje metryki. Największa i najłatwiej dostępna pula kandydatów; kluczowa jest umiejętność zadawania pytań, nie sam SQL.",
                },
                {
                  title: "Analytics Engineer",
                  meta: "dbt, modelowanie, testy danych",
                  body: "Warstwa między surowymi danymi a raportem: modele, testy jakości, dokumentacja, wersjonowanie. Najszybciej rosnąca rola w Polsce i zwykle najlepszy pierwszy hire po analityku.",
                },
                {
                  title: "Data Engineer",
                  meta: "Pipeline'y, orkiestracja, infrastruktura",
                  body: "Ingest, streaming, Airflow/Dagster, Spark, koszty i SLA. Najtrudniejszy do zweryfikowania w rozmowie, bo najwięcej wartości leży w decyzjach architektonicznych.",
                },
              ],
            },
            {
              kind: "p",
              text: "Najczęstszy kosztowny błąd: zatrudnienie Data Engineera jako pierwszej osoby w firmie bez zdefiniowanych metryk. Powstaje wtedy dopracowana platforma, z której nikt nie korzysta. W większości organizacji lepszą kolejnością jest analityk → analytics engineer → data engineer → platforma.",
            },
          ],
        },
        {
          id: "stack",
          eyebrow: "03 / Stack",
          title: "Czego szukać w 2026 roku",
          blocks: [
            {
              kind: "list",
              items: [
                "Hurtownia: Snowflake, BigQuery lub Databricks - w polskich ofertach dominują dziś te trzy, a doświadczenie migracyjne między nimi jest cenniejsze niż lojalność wobec jednej.",
                "Transformacje: dbt jako standard de facto, coraz częściej z SQLMesh w większych zespołach.",
                "Orkiestracja: Airflow pozostaje domyślny, Dagster i Prefect rosną w produktowych zespołach.",
                "Streaming: Kafka i Flink w fintechu, logistyce i e-commerce - to segment o najmniejszej podaży kandydatów.",
                "Zarządzanie: contracts, lineage, katalog danych i FinOps dla chmury. Ten obszar coraz częściej decyduje o awansie na seniora.",
              ],
            },
          ],
        },
        {
          id: "weryfikacja",
          eyebrow: "04 / Weryfikacja",
          title: "Jak sprawdzać kandydatów data bez zadań na osiem godzin",
          blocks: [
            {
              kind: "list",
              items: [
                "Daj realny, brzydki fragment schematu z Twojej firmy i poproś o zaprojektowanie modelu na żywo - 45 minut wystarczy, by odróżnić modelowanie od klepania SQL-a.",
                "Zapytaj o incydent: co się zepsuło w pipelinie, jak został wykryty i co zmieniło się później w procesie.",
                "Poproś o oszacowanie kosztu rozwiązania. Świadomość kosztowa najszybciej pokazuje, ile ktoś naprawdę przepracował w danych.",
                "Sprawdź komunikację z odbiorcą biznesowym - w rolach data to najczęstsza przyczyna nieudanych zatrudnień.",
              ],
            },
          ],
        },
      ],
      faqTitle: "Najczęstsze pytania",
      faq: [
        {
          q: "Kogo zatrudnić jako pierwszą osobę do danych?",
          a: "Zwykle analityka z silnym SQL i zacięciem produktowym albo analytics engineera. Data engineer opłaca się, gdy istnieją już źródła, metryki i realne potrzeby raportowe.",
        },
        {
          q: "Ile trwa rekrutacja Data Engineera w Polsce?",
          a: "Przy jawnych widełkach i trzech etapach realnie 3-6 tygodni. Największym wąskim gardłem jest zwykle zbyt szeroki opis roli, nie brak kandydatów.",
        },
        {
          q: "Czy analytics engineer zastąpi data engineera?",
          a: "Nie. Analytics engineer pracuje w hurtowni na już dostarczonych danych; data engineer odpowiada za to, żeby dane tam trafiły w sposób niezawodny i tani.",
        },
      ],
      cta: ctaPl,
      methodology:
        "Metodologia: zakresy pochodzą z ofert zaakceptowanych w naszych procesach w latach 2025-2026 oraz z publicznych raportów płacowych polskiego rynku IT.",
    },
    en: {
      title: "Hiring data engineers and data teams",
      h1: "Hiring data engineers in Poland 2026",
      metaTitle: "Data Engineer Salary in Poland 2026: Hiring Guide",
      metaDescription:
        "What a data engineer costs in Poland in 2026, how analyst, analytics engineer and data engineer roles differ, which to hire first and how to assess candidates fast.",
      eyebrow: "Insights · Data",
      summary:
        "Pay, role boundaries and the right order to build a data team - from first analyst to a full platform.",
      lead:
        "Data teams in Poland are growing faster than any area except AI, yet data hiring is unusually prone to mis-fit: analyst, data engineer and analytics engineer are three different professions that job ads still merge into one.",
      note:
        "Monthly figures in PLN. UoP is gross employment; B2B is net invoice excluding VAT. Based on processes run across Poland and CEE.",
      readTime: "8 min read",
      takeaways: [
        "A senior data engineer in Poland earns 21,000-30,000 PLN gross on employment or 25,000-35,000 PLN net on B2B (2026).",
        "Analytics engineer (dbt) is the fastest-growing data role in Poland at 14,000-20,000 PLN gross.",
        "Analyst, analytics engineer and data engineer are three distinct professions - for most companies the right order is analyst, analytics engineer, data engineer, then platform.",
        "The pay premium comes from cost ownership: an engineer who cuts the warehouse bill by 30% is valued above a longer tool list on the CV.",
        "Hiring a data engineer takes 3-6 weeks in practice when the range is public and the process has three stages.",
        "Candidate supply is thinnest in streaming (Kafka, Flink) across fintech, logistics and e-commerce.",
      ],
      sections: [
        {
          id: "pay",
          eyebrow: "01 / Compensation",
          title: "Data role salaries",
          blocks: [
            {
              kind: "table",
              caption: "Data role salary ranges in Poland, 2026",
              head: ["Role", "Employment (gross/mo)", "B2B (net/mo)", "B2B hourly"],
              rows: [
                ["Data Analyst (mid)", "9 000 - 14 000 PLN", "11 000 - 16 000 PLN", "80 - 120 PLN"],
                ["Analytics Engineer (dbt)", "14 000 - 20 000 PLN", "17 000 - 24 000 PLN", "130 - 180 PLN"],
                ["Data Engineer (mid)", "15 000 - 21 000 PLN", "18 000 - 25 000 PLN", "140 - 190 PLN"],
                ["Senior Data Engineer", "21 000 - 30 000 PLN", "25 000 - 35 000 PLN", "180 - 250 PLN"],
                ["Data Platform / Big Data Engineer", "23 000 - 32 000 PLN", "27 000 - 38 000 PLN", "200 - 270 PLN"],
                ["Head of Data", "28 000 - 40 000 PLN", "33 000 - 48 000 PLN", "-"],
              ],
            },
            {
              kind: "p",
              text: "The pay premium in data comes from scale and cost ownership, not tool familiarity. An engineer who can cut a warehouse bill by 30% is valued above someone with a longer technology list on the CV.",
            },
          ],
        },
        {
          id: "roles",
          eyebrow: "02 / Roles",
          title: "Three professions, one job ad",
          blocks: [
            {
              kind: "cards",
              items: [
                {
                  title: "Data Analyst",
                  meta: "SQL, BI and business context",
                  body: "Answers business questions, builds dashboards, defines metrics. The largest and most accessible pool; the differentiator is asking the right question, not SQL syntax.",
                },
                {
                  title: "Analytics Engineer",
                  meta: "dbt, modelling, data tests",
                  body: "The layer between raw data and the report: models, quality tests, documentation, versioning. The fastest-growing role in Poland and usually the best hire after the first analyst.",
                },
                {
                  title: "Data Engineer",
                  meta: "Pipelines, orchestration, infrastructure",
                  body: "Ingestion, streaming, Airflow/Dagster, Spark, cost and SLAs. The hardest to assess in interview, because most of the value sits in architectural judgement.",
                },
              ],
            },
            {
              kind: "p",
              text: "The most expensive common mistake is hiring a data engineer first, before metrics exist. You get a polished platform nobody uses. For most organisations the better order is analyst → analytics engineer → data engineer → platform.",
            },
          ],
        },
        {
          id: "stack",
          eyebrow: "03 / Stack",
          title: "What to look for in 2026",
          blocks: [
            {
              kind: "list",
              items: [
                "Warehouse: Snowflake, BigQuery or Databricks dominate Polish job ads; migration experience between them beats loyalty to one.",
                "Transformation: dbt as the de facto standard, increasingly alongside SQLMesh in larger teams.",
                "Orchestration: Airflow remains the default, with Dagster and Prefect growing in product teams.",
                "Streaming: Kafka and Flink in fintech, logistics and e-commerce - the segment with the thinnest candidate supply.",
                "Governance: contracts, lineage, catalogues and cloud FinOps. This area increasingly decides who counts as senior.",
              ],
            },
          ],
        },
        {
          id: "assessment",
          eyebrow: "04 / Assessment",
          title: "Assessing data candidates without eight-hour tasks",
          blocks: [
            {
              kind: "list",
              items: [
                "Share a real, ugly fragment of your schema and model it together live - 45 minutes separates modelling from SQL typing.",
                "Ask about an incident: what broke in a pipeline, how it was detected, and what changed in the process afterwards.",
                "Ask them to estimate the cost of their solution. Cost awareness is the fastest seniority test in data.",
                "Check communication with a business stakeholder - in data roles that is the most common cause of failed hires.",
              ],
            },
          ],
        },
      ],
      faqTitle: "Frequently asked questions",
      faq: [
        {
          q: "Who should be your first data hire?",
          a: "Usually an analyst with strong SQL and product instinct, or an analytics engineer. A data engineer pays off once sources, metrics and real reporting demand already exist.",
        },
        {
          q: "How long does hiring a data engineer take in Poland?",
          a: "With a public range and three stages, realistically 3-6 weeks. The usual bottleneck is an overly broad role definition, not candidate supply.",
        },
        {
          q: "Does an analytics engineer replace a data engineer?",
          a: "No. Analytics engineers work inside the warehouse on data already delivered; data engineers make sure it arrives reliably and cheaply.",
        },
      ],
      cta: ctaEn,
      methodology:
        "Methodology: ranges come from offers accepted in our 2025-2026 processes and public Polish IT salary reports.",
    },
    zh: {
      title: "招聘 Data Engineer 及数据团队",
      h1: "2026 年波兰 Data Engineer 招聘指南",
      metaTitle: "2026 年波兰 Data Engineer 薪资：招聘指南",
      metaDescription:
        "2026 年波兰 Data Engineer 的薪资水平、analyst、analytics engineer 与 data engineer 三种角色的区别、优先招聘顺序，以及如何快速评估候选人。",
      eyebrow: "洞察 · 数据",
      summary:
        "薪资水平、数据岗位角色边界，以及从第一位分析师到完整数据平台的正确搭建顺序。",
      lead:
        "波兰的数据团队正以仅次于 AI 的速度快速扩张，但数据类招聘格外容易出现岗位错配：analyst、data engineer 和 analytics engineer 是三种截然不同的职业，但招聘广告仍常常把它们混为一谈。",
      note:
        "金额为月度，单位 PLN。UoP 为税前工资；B2B 为不含增值税的净额。数据来自我们在波兰及 CEE 地区的招聘实践。",
      readTime: "阅读时长 8 分钟",
      takeaways: [
        "2026 年波兰高级 Data Engineer 的薪资为 UoP 税前 21,000-30,000 PLN,或 B2B 净额 25,000-35,000 PLN。",
        "Analytics Engineer（dbt 方向）是波兰增长最快的数据岗位，税前薪资为 14,000-20,000 PLN。",
        "Analyst、analytics engineer 和 data engineer 是三种不同职业，对大多数公司而言，正确的招聘顺序是 analyst、analytics engineer、data engineer,之后才是平台建设。",
        "薪资溢价来自成本责任：能把数据仓库账单降低 30% 的工程师，比简历上技术清单更长的人更值钱。",
        "在薪资区间公开且流程为三轮的情况下,Data Engineer 招聘实际耗时 3-6 周。",
        "候选人供给最紧张的领域是 fintech、物流和电商中的流式处理岗位（Kafka、Flink）。",
      ],
      sections: [
        {
          id: "widelki",
          eyebrow: "01 / 薪资",
          title: "数据岗位薪资水平",
          blocks: [
            {
              kind: "table",
              caption: "2026 年波兰数据岗位薪资区间",
              head: ["职位", "雇佣合同（税前/月）", "B2B（净额/月）", "B2B 时薪"],
              rows: [
                ["Data Analyst（中级）", "9 000 - 14 000 PLN", "11 000 - 16 000 PLN", "80 - 120 PLN"],
                ["Analytics Engineer（dbt）", "14 000 - 20 000 PLN", "17 000 - 24 000 PLN", "130 - 180 PLN"],
                ["Data Engineer（中级）", "15 000 - 21 000 PLN", "18 000 - 25 000 PLN", "140 - 190 PLN"],
                ["高级 Data Engineer", "21 000 - 30 000 PLN", "25 000 - 35 000 PLN", "180 - 250 PLN"],
                ["数据平台 / 大数据工程师", "23 000 - 32 000 PLN", "27 000 - 38 000 PLN", "200 - 270 PLN"],
                ["数据负责人（Head of Data）", "28 000 - 40 000 PLN", "33 000 - 48 000 PLN", "-"],
              ],
            },
            {
              kind: "p",
              text: "数据领域的薪资溢价来自业务规模和成本责任，而非单纯的工具熟练度。能把数据仓库账单降低 30% 的工程师，估值高于简历上技术清单更长的人。",
            },
          ],
        },
        {
          id: "role",
          eyebrow: "02 / 角色",
          title: "三种职业，一条招聘广告",
          blocks: [
            {
              kind: "cards",
              items: [
                {
                  title: "Data Analyst",
                  meta: "SQL + BI + 业务理解",
                  body: "回答业务问题、搭建看板、定义指标。人才池最大、最容易触达；关键能力是提出正确的问题，而不是 SQL 语法本身。",
                },
                {
                  title: "Analytics Engineer",
                  meta: "dbt、建模、数据测试",
                  body: "处于原始数据和报表之间的中间层：建模、质量测试、文档、版本管理。波兰增长最快的角色，通常是继分析师之后最合适的第一个招聘岗位。",
                },
                {
                  title: "Data Engineer",
                  meta: "Pipeline、编排、基础设施",
                  body: "数据接入、流式处理、Airflow/Dagster、Spark、成本与 SLA。面试中最难评估，因为大部分价值体现在架构决策上。",
                },
              ],
            },
            {
              kind: "p",
              text: "最常见也代价最高的错误：在指标体系尚未建立时就先招 Data Engineer。结果是搭建出一套精致的平台，却无人使用。对大多数组织而言，更合理的顺序是 analyst → analytics engineer → data engineer → 平台。",
            },
          ],
        },
        {
          id: "stack",
          eyebrow: "03 / 技术栈",
          title: "2026 年应该关注哪些能力",
          blocks: [
            {
              kind: "list",
              items: [
                "数据仓库：Snowflake、BigQuery 或 Databricks 是波兰招聘广告中的主流三选项，拥有跨平台迁移经验比只忠于一种工具更有价值。",
                "转换层：dbt 已成为事实标准，大型团队中越来越多地搭配使用 SQLMesh。",
                "编排：Airflow 仍是默认选择,Dagster 和 Prefect 在产品型团队中增长明显。",
                "流式处理：Kafka 和 Flink 在 fintech、物流和电商领域应用广泛，这是候选人供给最紧张的细分领域。",
                "治理：数据契约、血缘、数据目录以及云端 FinOps。这一领域正日益成为判断是否达到高级水平的关键因素。",
              ],
            },
          ],
        },
        {
          id: "weryfikacja",
          eyebrow: "04 / 评估",
          title: "不靠 8 小时任务如何评估数据候选人",
          blocks: [
            {
              kind: "list",
              items: [
                "拿出你公司真实的、有点“粗糙”的 schema 片段，当场一起建模，45 分钟就足以区分谁在做建模、谁只是在写 SQL。",
                "询问一次事故经历：pipeline 出了什么问题、是如何被发现的，以及事后流程做了哪些改变。",
                "让候选人估算方案的成本。成本意识是数据领域最快的资深度测试。",
                "考察与业务方的沟通能力，在数据岗位上，这是招聘失败最常见的原因。",
              ],
            },
          ],
        },
      ],
      faqTitle: "常见问题",
      faq: [
        {
          q: "数据团队的第一个招聘岗位应该是谁？",
          a: "通常是 SQL 能力强、具备产品意识的 analyst,或者 analytics engineer。当数据源、指标体系和真实报表需求已经就绪时，再招 data engineer 才划算。",
        },
        {
          q: "在波兰招聘 Data Engineer 需要多久？",
          a: "在薪资区间公开、流程为三轮的情况下，实际耗时 3-6 周。最常见的瓶颈是职位描述过于宽泛，而不是候选人供给不足。",
        },
        {
          q: "Analytics engineer 会取代 data engineer 吗？",
          a: "不会。Analytics engineer 在数据仓库内对已交付的数据进行加工；data engineer 负责确保数据能够可靠、低成本地送达那里。",
        },
      ],
      cta: ctaZh,
      methodology:
        "方法说明：数据区间来自我们 2025-2026 年招聘流程中被接受的 offer,以及波兰 IT 行业公开薪资报告。",
    },
  },

  {
    slug: "rekrutacja-devops-cloud",
    updated: "2026-07-09",
    updatedLabel: { pl: "Aktualizacja: 9 lipca 2026", en: "Last updated: 9 July 2026", zh: "最近更新：2026 年 7 月 9 日" },
    topic: { pl: "DevOps / Cloud", en: "DevOps / Cloud", zh: "DevOps / 云计算" },
    pl: {
      title: "Rekrutacja DevOps, SRE i platform engineering",
      h1: "Rekrutacja DevOps i SRE w Polsce 2026",
      metaTitle: "Rekrutacja DevOps i SRE w Polsce 2026: widełki i taktyki",
      metaDescription:
        "Ile kosztuje DevOps, SRE i platform engineer w Polsce w 2026, jak skrócić proces rekrutacyjny do dwóch tygodni i czym wygrywać, gdy nie możesz przelicytować budżetem.",
      eyebrow: "Insights · Infrastruktura",
      summary:
        "Najciaśniejszy segment rynku: widełki, taktyka szybkiego procesu i argumenty poza pieniędzmi.",
      lead:
        "DevOps i SRE to segment, w którym kandydat prowadzi zwykle dwa lub trzy procesy równolegle. Wygrywa nie ten, kto zapłaci najwięcej, tylko ten, kto zdecyduje najszybciej i najlepiej opisze realia pracy.",
      note: "Kwoty miesięczne w PLN. UoP - brutto; B2B - netto bez VAT.",
      readTime: "7 min czytania",
      takeaways: [
        "Senior DevOps / SRE w Polsce kosztuje 22 000 - 30 000 PLN brutto na UoP lub 26 000 - 36 000 PLN netto na B2B (2026).",
        "To najciaśniejszy segment polskiego IT - kandydat prowadzi zwykle 2-3 procesy równolegle.",
        "Decyzja w 5 dni roboczych wygrywa częściej niż podniesienie oferty o 2 000 PLN.",
        "Jawne widełki w pierwszej wiadomości podwajają odsetek odpowiedzi kandydatów pasywnych (z ok. 20% do ok. 40%).",
        "Wymóg pracy z biura zawęża pulę kandydatów o ponad połowę.",
        "Trzy etapy techniczne to w tym segmencie strata finalistów - optimum to dwa etapy plus rozmowa z zespołem.",
      ],
      sections: [
        {
          id: "widelki",
          eyebrow: "01 / Wynagrodzenia",
          title: "Widełki DevOps, SRE i platform",
          blocks: [
            {
              kind: "table",
              caption: "Widełki wynagrodzeń DevOps/SRE, Polska 2026",
              head: ["Rola", "UoP (brutto/mc)", "B2B (netto/mc)", "Stawka godzinowa B2B"],
              rows: [
                ["Junior DevOps (0-2 lata)", "8 000 - 12 000 PLN", "10 000 - 14 000 PLN", "80 - 110 PLN"],
                ["DevOps Engineer (mid)", "16 000 - 22 000 PLN", "19 000 - 26 000 PLN", "150 - 200 PLN"],
                ["Senior DevOps / SRE", "22 000 - 30 000 PLN", "26 000 - 36 000 PLN", "190 - 260 PLN"],
                ["Platform Engineer", "23 000 - 32 000 PLN", "27 000 - 38 000 PLN", "200 - 270 PLN"],
                ["Cloud Architect", "26 000 - 36 000 PLN", "30 000 - 44 000 PLN", "220 - 300 PLN"],
                ["Security / DevSecOps Engineer", "22 000 - 33 000 PLN", "26 000 - 40 000 PLN", "200 - 280 PLN"],
              ],
            },
            {
              kind: "p",
              text: "Oferty poniżej środka widełek w tym segmencie konwertują rzadko. Jeśli budżet jest sztywny, szybszą dźwignią jest tempo procesu: decyzja w 5 dni roboczych wygrywa częściej niż dodatkowe 2 000 PLN.",
            },
          ],
        },
        {
          id: "sygnaly",
          eyebrow: "02 / Weryfikacja",
          title: "Sygnały, które realnie różnicują kandydatów",
          blocks: [
            {
              kind: "list",
              items: [
                "Produkcyjny Kubernetes z realnym incydentem na koncie - nie klaster testowy z tutoriala.",
                "Terraform z modułami i stanem współdzielonym w zespole, a nie pojedyncze pliki.",
                "Świadomość kosztów chmury: czy kandydat potrafi wskazać, co w Twoim rachunku byłoby pierwsze do obcięcia.",
                "On-call: doświadczenie w rotacji, postmortemy, kultura błędu bez szukania winnego.",
                "Bezpieczeństwo dostaw: podpisywanie artefaktów, SBOM, skanowanie obrazów - coraz częściej wymóg w projektach objętych NIS2.",
              ],
            },
          ],
        },
        {
          id: "taktyka",
          eyebrow: "03 / Taktyka",
          title: "Jak wygrać, kiedy nie możesz przelicytować",
          blocks: [
            {
              kind: "list",
              items: [
                "Opisz realny stan infrastruktury, łącznie z długiem technicznym. Uczciwy opis roli przyciąga ludzi, którzy lubią porządkować, i odsiewa rozczarowania w trzecim miesiącu.",
                "Pokaż zakres decyzyjności: co kandydat będzie mógł zmienić bez pytania o zgodę.",
                "Ogranicz proces do dwóch etapów plus rozmowa z zespołem. Trzy etapy techniczne to w tym segmencie strata finalistów.",
                "Zaoferuj elastyczność zdalną - praca zdalna jest w tej roli oczekiwana, a wymóg biura zawęża pulę o ponad połowę.",
                "Podaj widełki w pierwszej wiadomości. Bez nich odpowiada ok. 20% kandydatów pasywnych, z nimi - dwa razy więcej.",
              ],
            },
          ],
        },
      ],
      faqTitle: "Najczęstsze pytania",
      faq: [
        {
          q: "Dlaczego rekrutacja DevOps trwa tak długo?",
          a: "Bo podaż jest najniższa w całym IT, a kandydaci prowadzą kilka procesów naraz. Skrócenie procesu do dwóch tygodni jest zwykle skuteczniejsze niż podniesienie budżetu.",
        },
        {
          q: "DevOps czy Platform Engineer - czym się różnią?",
          a: "DevOps zwykle wspiera zespoły produktowe w dostarczaniu, platform engineer buduje wewnętrzny produkt (self-service, golden path) używany przez te zespoły. Druga rola wymaga myślenia produktowego.",
        },
        {
          q: "Czy warto rozważyć kontraktora zamiast etatu?",
          a: "Tak, przy migracjach i projektach o jasnym końcu. Do utrzymania i on-call lepiej sprawdza się stały członek zespołu.",
        },
      ],
      cta: ctaPl,
      methodology:
        "Metodologia: dane z procesów DevOps/SRE prowadzonych przez Positive Recruitment w Polsce i regionie CEE w latach 2025-2026.",
    },
    en: {
      title: "Hiring DevOps, SRE and platform engineers",
      h1: "Hiring DevOps and SRE in Poland 2026",
      metaTitle: "Hiring DevOps & SRE in Poland 2026: Pay and Tactics",
      metaDescription:
        "What DevOps, SRE and platform engineers cost in Poland in 2026, how to compress hiring to two weeks, and what wins candidates when you cannot outbid on salary.",
      eyebrow: "Insights · Infrastructure",
      summary:
        "The tightest segment of the market: ranges, fast-process tactics and the arguments that work beyond money.",
      lead:
        "In DevOps and SRE a candidate typically runs two or three processes in parallel. The winner is rarely the highest bidder - it is the company that decides fastest and describes the day-to-day reality most honestly.",
      note: "Monthly figures in PLN. UoP is gross employment; B2B is net invoice excluding VAT.",
      readTime: "7 min read",
      takeaways: [
        "A senior DevOps / SRE engineer in Poland costs 22,000-30,000 PLN gross on employment or 26,000-36,000 PLN net on B2B (2026).",
        "This is the tightest segment of Polish IT - candidates typically run two or three processes in parallel.",
        "A decision within 5 working days wins more often than adding 2,000 PLN to the offer.",
        "A transparent range in the first message roughly doubles passive-candidate reply rates (from about 20% to about 40%).",
        "Requiring full on-site work cuts the available pool by more than half.",
        "Three technical stages lose finalists here - the optimum is two stages plus a team conversation.",
      ],
      sections: [
        {
          id: "pay",
          eyebrow: "01 / Compensation",
          title: "DevOps, SRE and platform ranges",
          blocks: [
            {
              kind: "table",
              caption: "DevOps/SRE salary ranges in Poland, 2026",
              head: ["Role", "Employment (gross/mo)", "B2B (net/mo)", "B2B hourly"],
              rows: [
                ["Junior DevOps (0-2 yrs)", "8 000 - 12 000 PLN", "10 000 - 14 000 PLN", "80 - 110 PLN"],
                ["DevOps Engineer (mid)", "16 000 - 22 000 PLN", "19 000 - 26 000 PLN", "150 - 200 PLN"],
                ["Senior DevOps / SRE", "22 000 - 30 000 PLN", "26 000 - 36 000 PLN", "190 - 260 PLN"],
                ["Platform Engineer", "23 000 - 32 000 PLN", "27 000 - 38 000 PLN", "200 - 270 PLN"],
                ["Cloud Architect", "26 000 - 36 000 PLN", "30 000 - 44 000 PLN", "220 - 300 PLN"],
                ["Security / DevSecOps Engineer", "22 000 - 33 000 PLN", "26 000 - 40 000 PLN", "200 - 280 PLN"],
              ],
            },
            {
              kind: "p",
              text: "Offers below the mid-point rarely convert in this segment. Where budget is fixed, process speed is the faster lever: a decision within five working days beats an extra 2 000 PLN more often than not.",
            },
          ],
        },
        {
          id: "signals",
          eyebrow: "02 / Assessment",
          title: "Signals that actually separate candidates",
          blocks: [
            {
              kind: "list",
              items: [
                "Production Kubernetes with a real incident behind them - not a tutorial cluster.",
                "Terraform with modules and shared state across a team, rather than one-off files.",
                "Cloud cost awareness: can they name what they would cut first in your bill.",
                "On-call: rotation experience, postmortems, blameless incident culture.",
                "Supply-chain security: artifact signing, SBOM, image scanning - increasingly required in NIS2-scoped projects.",
              ],
            },
          ],
        },
        {
          id: "tactics",
          eyebrow: "03 / Tactics",
          title: "How to win when you cannot outbid",
          blocks: [
            {
              kind: "list",
              items: [
                "Describe the real state of the infrastructure, technical debt included. An honest brief attracts people who enjoy fixing things and prevents month-three disappointment.",
                "Show the decision scope: what the hire can change without asking permission.",
                "Cap the process at two stages plus a team conversation. Three technical stages loses finalists in this segment.",
                "Offer remote flexibility - it is expected here, and an on-site requirement cuts the pool by more than half.",
                "Put the range in the first message. Without it around 20% of passive candidates reply; with it, roughly twice as many.",
              ],
            },
          ],
        },
      ],
      faqTitle: "Frequently asked questions",
      faq: [
        {
          q: "Why does DevOps hiring take so long?",
          a: "Supply is the lowest in IT and candidates run several processes at once. Compressing your process to two weeks usually beats raising the budget.",
        },
        {
          q: "DevOps or platform engineer - what is the difference?",
          a: "DevOps typically supports product teams in delivery; a platform engineer builds an internal product (self-service, golden paths) those teams consume. The second requires product thinking.",
        },
        {
          q: "Should we consider a contractor instead of a permanent hire?",
          a: "Yes for migrations and projects with a clear end. For maintenance and on-call, a permanent team member works better.",
        },
      ],
      cta: ctaEn,
      methodology:
        "Methodology: based on DevOps/SRE processes run by Positive Recruitment across Poland and CEE in 2025-2026.",
    },
    zh: {
      title: "招聘 DevOps、SRE 和平台工程师",
      h1: "2026 年波兰 DevOps 和 SRE 招聘指南",
      metaTitle: "2026 年波兰 DevOps 与 SRE 招聘：薪资与策略",
      metaDescription:
        "2026 年波兰 DevOps、SRE 和平台工程师的薪资水平，如何将招聘周期压缩到两周，以及在无法用薪资取胜时能打动候选人的方法。",
      eyebrow: "洞察 · 基础设施",
      summary:
        "市场上竞争最激烈的细分领域：薪资区间、快速流程策略，以及金钱之外真正有效的说服点。",
      lead:
        "在 DevOps 和 SRE 领域，候选人通常同时手握两到三个进行中的招聘流程。最终胜出的往往不是出价最高的公司，而是决策最快、把日常工作真实情况讲得最清楚的公司。",
      note: "金额为月度，单位 PLN。UoP 为税前工资；B2B 为不含增值税的净额。",
      readTime: "阅读时长 7 分钟",
      takeaways: [
        "2026 年波兰高级 DevOps / SRE 工程师的薪资为 UoP 税前 22,000-30,000 PLN,或 B2B 净额 26,000-36,000 PLN。",
        "这是波兰 IT 领域竞争最激烈的细分市场，候选人通常同时手握 2-3 个进行中的流程。",
        "5 个工作日内做出决定，比多加 2,000 PLN 更能赢得候选人。",
        "在首次接触时公开薪资区间，可使被动候选人的回复率大约翻倍（从约 20% 提高到约 40%）。",
        "要求到岗办公会使可用候选人池缩小超过一半。",
        "三轮技术面试会流失候选人，在这个领域，最佳配置是两轮技术面加一次团队交流。",
      ],
      sections: [
        {
          id: "widelki",
          eyebrow: "01 / 薪资",
          title: "DevOps、SRE 和平台工程师薪资区间",
          blocks: [
            {
              kind: "table",
              caption: "2026 年波兰 DevOps/SRE 薪资区间",
              head: ["职位", "雇佣合同（税前/月）", "B2B（净额/月）", "B2B 时薪"],
              rows: [
                ["初级 DevOps（0-2 年）", "8 000 - 12 000 PLN", "10 000 - 14 000 PLN", "80 - 110 PLN"],
                ["DevOps Engineer（中级）", "16 000 - 22 000 PLN", "19 000 - 26 000 PLN", "150 - 200 PLN"],
                ["高级 DevOps / SRE", "22 000 - 30 000 PLN", "26 000 - 36 000 PLN", "190 - 260 PLN"],
                ["平台工程师", "23 000 - 32 000 PLN", "27 000 - 38 000 PLN", "200 - 270 PLN"],
                ["云架构师", "26 000 - 36 000 PLN", "30 000 - 44 000 PLN", "220 - 300 PLN"],
                ["安全 / DevSecOps 工程师", "22 000 - 33 000 PLN", "26 000 - 40 000 PLN", "200 - 280 PLN"],
              ],
            },
            {
              kind: "p",
              text: "在这个细分领域，低于薪资区间中位数的 offer 很少能成功转化。如果预算固定，更快见效的杠杆是流程速度：5 个工作日内做出决定，往往比多给 2,000 PLN 更管用。",
            },
          ],
        },
        {
          id: "sygnaly",
          eyebrow: "02 / 评估",
          title: "真正能区分候选人的信号",
          blocks: [
            {
              kind: "list",
              items: [
                "有过真实事故处理经历的生产级 Kubernetes 经验，而不是照着教程搭的测试集群。",
                "带模块化设计、团队共享 state 的 Terraform,而不是零散的单个文件。",
                "云成本意识：候选人能否指出你账单里第一个该砍掉的项目。",
                "On-call 经验：轮值经验、postmortem 复盘、不追责的事故文化。",
                "供应链安全：产物签名、SBOM、镜像扫描，在 NIS2 相关项目中正日益成为硬性要求。",
              ],
            },
          ],
        },
        {
          id: "taktyka",
          eyebrow: "03 / 策略",
          title: "在无法出价更高时如何取胜",
          blocks: [
            {
              kind: "list",
              items: [
                "如实描述基础设施现状，包括技术债。诚实的 brief 能吸引喜欢整理和优化的人，并避免第三个月出现的失望。",
                "说明决策自主权：候选人在不需要请示的情况下能改变哪些事情。",
                "把流程控制在两轮技术面加一次团队交流。在这个领域，三轮技术面试会流失候选人。",
                "提供远程办公的灵活性，这在该岗位上是默认预期，要求到岗办公会使人才池缩小超过一半。",
                "在首次消息中给出薪资区间。不给出区间时约 20% 的被动候选人会回复，给出区间后这一比例会翻倍。",
              ],
            },
          ],
        },
      ],
      faqTitle: "常见问题",
      faq: [
        {
          q: "为什么 DevOps 招聘周期这么长？",
          a: "因为整个 IT 行业中这类候选人的供给最少，而且他们通常同时进行多个流程。把流程压缩到两周，通常比提高预算更有效。",
        },
        {
          q: "DevOps 和平台工程师有什么区别？",
          a: "DevOps 通常在交付环节支持产品团队，而平台工程师负责构建供这些团队使用的内部产品（self-service、golden path）。后者需要更强的产品思维。",
        },
        {
          q: "值得考虑用合同工代替正式员工吗？",
          a: "在迁移项目和有明确终点的项目中值得考虑。但对于日常运维和 on-call,固定团队成员的效果更好。",
        },
      ],
      cta: ctaZh,
      methodology:
        "方法说明：数据来自 Positive Recruitment 在 2025-2026 年于波兰及 CEE 地区完成的 DevOps/SRE 招聘流程。",
    },
  },

  {
    slug: "b2b-czy-uop-zatrudnianie-programistow",
    updated: "2026-08-05",
    updatedLabel: { pl: "Aktualizacja: 5 sierpnia 2026", en: "Last updated: 5 August 2026", zh: "最近更新：2026 年 8 月 5 日" },
    topic: { pl: "Zatrudnianie", en: "Hiring", zh: "招聘雇佣" },
    pl: {
      title: "B2B czy UoP: jak zatrudnić programistę w Polsce",
      h1: "B2B czy umowa o pracę? Zatrudnianie programistów w Polsce",
      metaTitle: "B2B czy umowa o pracę? Zatrudnianie IT w Polsce 2026",
      metaDescription:
        "B2B czy UoP przy zatrudnianiu programisty w Polsce w 2026: realny koszt pracodawcy, ryzyko pozornego samozatrudnienia, EOR bez spółki i wybór formy pod rolę.",
      eyebrow: "Insights · Zatrudnianie",
      summary:
        "Realny koszt pracodawcy, ryzyka prawne, EOR bez polskiej spółki i wybór formy pod rolę.",
      lead:
        "Zagraniczne firmy zatrudniające w Polsce najczęściej pytają nas o jedno: B2B czy umowa o pracę. Odpowiedź zależy od doświadczenia kandydata, planowanej długości współpracy i tego, czy macie w Polsce spółkę. Poniżej liczby i konsekwencje, bez teorii.",
      note:
        "Materiał ma charakter informacyjny i nie stanowi porady prawnej ani podatkowej. Przed decyzją skonsultuj konkretny model z doradcą.",
      readTime: "8 min czytania",
      takeaways: [
        "Koszt pracodawcy przy UoP to około 1,2 × wynagrodzenie brutto plus świadczenia pozapłacowe.",
        "Stawka B2B jest zwykle o 15-25% wyższa niż odpowiadające jej brutto na UoP - kandydat wycenia w niej brak urlopu i składek.",
        "Większość polskich seniorów pracuje na B2B; brak tej opcji ogranicza pulę kandydatów o kilkadziesiąt procent.",
        "Employer of Record pozwala zatrudnić w Polsce bez własnej spółki, typowo za 8-15% narzutu na brutto.",
        "Własna spółka bywa tańsza niż EOR powyżej około 10 osób.",
        "B2B o cechach stosunku pracy (sztywne godziny, brak samodzielności, jeden zleceniodawca przez lata) grozi zakwestionowaniem formy współpracy i doszacowaniem składek.",
      ],
      sections: [
        {
          id: "porownanie",
          eyebrow: "01 / Porównanie",
          title: "Co realnie różni obie formy",
          blocks: [
            {
              kind: "table",
              caption: "B2B a umowa o pracę w polskim IT, 2026",
              head: ["Kryterium", "Umowa o pracę (UoP)", "Kontrakt B2B"],
              rows: [
                ["Koszt pracodawcy", "ok. 1,2 × brutto + świadczenia pozapłacowe", "kwota z faktury (netto + VAT do odliczenia)"],
                ["Urlop płatny", "26 dni + zwolnienia chorobowe", "brak, chyba że zapisany w umowie"],
                ["Okres wypowiedzenia", "do 3 miesięcy przy dłuższym stażu", "zwykle 1 miesiąc, ustalany umownie"],
                ["Preferencja kandydatów", "juniorzy, część midów, role z on-call", "większość seniorów i kontraktorów"],
                ["Własność IP", "domyślnie regulowana kodeksowo", "wymaga wyraźnych zapisów w umowie"],
                ["Elastyczność skalowania", "niska", "wysoka"],
              ],
            },
          ],
        },
        {
          id: "koszt",
          eyebrow: "02 / Budżet",
          title: "Jak policzyć realny koszt zatrudnienia",
          blocks: [
            {
              kind: "list",
              items: [
                "UoP: pomnóż brutto przez ok. 1,2, aby uzyskać koszt pracodawcy, a następnie doliczaj 26 dni urlopu, chorobowe i okresy wypowiedzenia.",
                "B2B: faktura jest kosztem, ale zwykle o 15-25% wyższym niż odpowiadające jej brutto na UoP - kandydat wycenia w niej brak urlopu i świadczeń.",
                "Świadczenia dodatkowe: prywatna opieka medyczna, karta sportowa i budżet szkoleniowy to razem 400-900 PLN miesięcznie na osobę i przy UoP są standardem, nie przewagą.",
                "Rekrutacja: koszt wakatu liczony w opóźnieniu roadmapy jest zwykle wyższy niż jakakolwiek prowizja agencji - policz go, zanim zaczniesz oszczędzać na procesie.",
              ],
            },
          ],
        },
        {
          id: "ryzyka",
          eyebrow: "03 / Ryzyka",
          title: "Gdzie B2B bywa ryzykowne",
          blocks: [
            {
              kind: "p",
              text: "Ryzyko pojawia się, gdy współpraca B2B ma cechy stosunku pracy: sztywne godziny, stałe podporządkowanie, brak samodzielności i praca wyłącznie dla jednego zleceniodawcy przez lata. W Polsce może to prowadzić do zakwestionowania formy współpracy i doszacowania składek. Praktyczne minimum higieny: realna samodzielność wykonawcza, jasny zakres rezultatu, wyraźne zapisy o przeniesieniu praw autorskich i uporządkowana kwestia poufności oraz RODO.",
            },
          ],
        },
        {
          id: "eor",
          eyebrow: "04 / Bez spółki",
          title: "Zatrudnianie w Polsce bez własnej spółki",
          blocks: [
            {
              kind: "list",
              items: [
                "Employer of Record: zatrudnienie na UoP przez podmiot trzeci, typowo 8-15% narzutu na brutto. Najszybszy start, pełna zgodność, wyższy koszt jednostkowy.",
                "Bezpośredni kontrakt B2B: możliwy bez polskiej spółki, popularny przy seniorach; wymaga starannej umowy i świadomości ryzyk opisanych wyżej.",
                "Własna spółka: powyżej ok. 10 osób zwykle tańsza niż EOR, ale wymaga księgowości, kadr i kilku tygodni na uruchomienie.",
              ],
            },
            {
              kind: "p",
              text: "W praktyce najczęściej rekomendujemy start przez EOR lub B2B przy pierwszych trzech-pięciu osobach i przejście na własny podmiot, gdy zespół w Polsce staje się trwałym elementem organizacji.",
            },
          ],
        },
        {
          id: "wybor",
          eyebrow: "05 / Decyzja",
          title: "Którą formę wybrać dla danej roli",
          blocks: [
            {
              kind: "cards",
              items: [
                { title: "Junior i mid", meta: "UoP", body: "Stabilność i mentoring są tu realnym argumentem sprzedażowym; UoP zwiększa liczbę odpowiedzi na ofertę." },
                { title: "Senior i architekt", meta: "B2B (opcjonalnie UoP)", body: "Większość seniorów w Polsce pracuje na B2B; brak tej opcji ogranicza pulę kandydatów o kilkadziesiąt procent." },
                { title: "Role z on-call", meta: "UoP", body: "Dyżury i odpowiedzialność operacyjna łatwiej ułożyć w ramach zatrudnienia niż kontraktu o rezultat." },
                { title: "Projekt o jasnym końcu", meta: "B2B", body: "Migracje, audyty, wdrożenia - elastyczność zakończenia współpracy jest tu największą wartością." },
              ],
            },
          ],
        },
      ],
      faqTitle: "Najczęstsze pytania",
      faq: [
        {
          q: "Czy zagraniczna firma może zatrudnić programistę w Polsce bez spółki?",
          a: "Tak - najczęściej przez Employer of Record (umowa o pracę) albo bezpośredni kontrakt B2B z jednoosobową działalnością kandydata.",
        },
        {
          q: "O ile wyższa jest stawka B2B niż brutto na UoP?",
          a: "Zwykle o 15-25%, ponieważ kandydat wlicza w nią brak płatnego urlopu, składek i świadczeń.",
        },
        {
          q: "Czy oferowanie tylko UoP zamyka drogę do seniorów?",
          a: "Nie zamyka, ale zawęża pulę. Przy wyłącznie etatowej ofercie warto wyrównać różnicę świadczeniami pozapłacowymi, budżetem szkoleniowym i przewidywalnym trybem pracy.",
        },
      ],
      cta: ctaPl,
      methodology:
        "Materiał informacyjny, przygotowany na bazie procesów rekrutacyjnych prowadzonych przez Positive Recruitment w Polsce w latach 2025-2026. Nie stanowi porady prawnej ani podatkowej.",
    },
    en: {
      title: "B2B or employment: how to hire developers in Poland",
      h1: "B2B or employment contract? Hiring developers in Poland",
      metaTitle: "B2B vs Employment: Hiring Developers in Poland 2026",
      metaDescription:
        "B2B or employment contract when hiring developers in Poland in 2026: true employer cost, misclassification risk, hiring via EOR without an entity, and role-by-role fit.",
      eyebrow: "Insights · Hiring",
      summary:
        "True employer cost, legal risk, hiring without a Polish entity, and which contract fits which role.",
      lead:
        "Foreign companies hiring in Poland almost always start with the same question: B2B or employment contract? The answer depends on seniority, expected duration and whether you have a Polish entity. Here are the numbers and consequences, without the theory.",
      note:
        "This is general information, not legal or tax advice. Validate your specific model with an adviser before committing.",
      readTime: "8 min read",
      takeaways: [
        "Employer cost on a Polish employment contract is roughly 1.2 x gross salary plus benefits.",
        "A B2B rate is usually 15-25% above the equivalent gross salary, because the candidate prices in missing leave and contributions.",
        "Most Polish senior developers work B2B; not offering it removes a large share of the available pool.",
        "An Employer of Record lets you hire in Poland without a local entity, typically at an 8-15% uplift on gross.",
        "Your own entity usually becomes cheaper than EOR above roughly 10 people.",
        "A B2B relationship with employment hallmarks (fixed hours, no autonomy, one client for years) risks reclassification and retroactive contributions.",
      ],
      sections: [
        {
          id: "comparison",
          eyebrow: "01 / Comparison",
          title: "What actually differs between the two",
          blocks: [
            {
              kind: "table",
              caption: "B2B vs employment in Polish IT, 2026",
              head: ["Criterion", "Employment (UoP)", "B2B contract"],
              rows: [
                ["Employer cost", "~1.2 × gross plus benefits", "the invoice amount (net, VAT recoverable)"],
                ["Paid leave", "26 days plus statutory sick leave", "none unless contracted"],
                ["Notice period", "up to 3 months with long tenure", "usually 1 month, set by contract"],
                ["Candidate preference", "juniors, some mids, on-call roles", "most seniors and contractors"],
                ["IP ownership", "largely covered by statute", "requires explicit contractual clauses"],
                ["Scaling flexibility", "low", "high"],
              ],
            },
          ],
        },
        {
          id: "budget",
          eyebrow: "02 / Budget",
          title: "Calculating the true cost of a hire",
          blocks: [
            {
              kind: "list",
              items: [
                "UoP: multiply gross by roughly 1.2 for employer cost, then add 26 days of leave, sick leave and notice periods.",
                "B2B: the invoice is the cost, but typically 15-25% above the equivalent gross salary - the candidate prices in the missing leave and contributions.",
                "Benefits: private healthcare, a sports card and a training budget run 400-900 PLN per person per month and are table stakes on employment offers, not a differentiator.",
                "Recruitment: the cost of a vacancy measured in delayed roadmap almost always exceeds any agency fee - calculate it before economising on the process.",
              ],
            },
          ],
        },
        {
          id: "risk",
          eyebrow: "03 / Risk",
          title: "Where B2B becomes risky",
          blocks: [
            {
              kind: "p",
              text: "Risk appears when a B2B relationship carries the hallmarks of employment: fixed hours, continuous subordination, no autonomy and years of work for a single client. In Poland this can lead to the arrangement being reclassified and contributions assessed retroactively. Practical hygiene: genuine execution autonomy, a clear deliverable scope, explicit IP assignment clauses, and properly handled confidentiality and GDPR terms.",
            },
          ],
        },
        {
          id: "eor",
          eyebrow: "04 / No entity",
          title: "Hiring in Poland without your own entity",
          blocks: [
            {
              kind: "list",
              items: [
                "Employer of Record: employment via a third party, typically an 8-15% uplift on gross. Fastest start, full compliance, higher unit cost.",
                "Direct B2B contract: possible without a Polish entity and common with seniors; requires a careful contract and awareness of the risks above.",
                "Own entity: usually cheaper than EOR above roughly 10 people, but needs accounting, HR and several weeks to set up.",
              ],
            },
            {
              kind: "p",
              text: "In practice we usually recommend starting through EOR or B2B for the first three to five people and moving to your own entity once the Polish team becomes a permanent part of the organisation.",
            },
          ],
        },
        {
          id: "choice",
          eyebrow: "05 / Decision",
          title: "Which model fits which role",
          blocks: [
            {
              kind: "cards",
              items: [
                { title: "Junior and mid", meta: "Employment", body: "Stability and mentoring are genuine selling points here, and employment raises reply rates on outreach." },
                { title: "Senior and architect", meta: "B2B (employment optional)", body: "Most Polish seniors work B2B; not offering it removes a large share of the available pool." },
                { title: "On-call roles", meta: "Employment", body: "Rotations and operational accountability sit more naturally inside employment than a deliverable-based contract." },
                { title: "Fixed-scope project", meta: "B2B", body: "Migrations, audits and rollouts - the ability to end cleanly is the main value." },
              ],
            },
          ],
        },
      ],
      faqTitle: "Frequently asked questions",
      faq: [
        {
          q: "Can a foreign company hire a developer in Poland without an entity?",
          a: "Yes - usually through an Employer of Record (employment contract) or a direct B2B contract with the candidate's sole proprietorship.",
        },
        {
          q: "How much higher is a B2B rate than gross employment pay?",
          a: "Typically 15-25%, because the candidate prices in the absence of paid leave, contributions and benefits.",
        },
        {
          q: "Does offering employment only rule out seniors?",
          a: "It does not rule them out, but it narrows the pool. If you can only offer employment, close the gap with benefits, a training budget and a predictable working model.",
        },
      ],
      cta: ctaEn,
      methodology:
        "General information based on recruitment processes run by Positive Recruitment in Poland in 2025-2026. Not legal or tax advice.",
    },
    zh: {
      title: "B2B 还是雇佣合同：如何在波兰招聘程序员",
      h1: "B2B 还是雇佣合同？在波兰招聘程序员",
      metaTitle: "B2B 还是雇佣合同？2026 年波兰 IT 招聘",
      metaDescription:
        "2026 年在波兰招聘程序员时该选 B2B 还是雇佣合同：真实的雇主成本、被认定为变相雇佣关系的风险、无需在波兰设立公司的 EOR 方案，以及按岗位选择合适的形式。",
      eyebrow: "洞察 · 雇佣",
      summary:
        "真实的雇主成本、法律风险、无需波兰实体的雇佣方式，以及不同岗位适合的合同形式。",
      lead:
        "在波兰招聘的境外公司几乎都会问同一个问题：B2B 还是雇佣合同？答案取决于岗位级别、合作预期时长，以及你是否在波兰拥有实体公司。以下是具体数字和相应影响，不谈理论。",
      note:
        "本文仅供参考，不构成法律或税务建议。做决定前请就具体方案咨询专业顾问。",
      readTime: "阅读时长 8 分钟",
      takeaways: [
        "雇佣合同（UoP）下的雇主成本约为税前工资的 1.2 倍。",
        "B2B 费率通常比对应的雇佣税前工资高 15%-25%，候选人会把没有带薪假期和社保缴纳计入其中。",
        "大多数波兰高级工程师采用 B2B 方式工作；不提供这一选项会使候选人池缩小数十个百分点。",
        "Employer of Record（EOR）可以让你在无需自建波兰公司的情况下完成雇佣，通常在税前工资基础上加收 8%-15%。",
        "当团队规模超过约 10 人时，自建公司通常比使用 EOR 更划算。",
        "带有雇佣关系特征的 B2B 合作（固定工时、缺乏自主性、多年只服务一个客户）存在被认定为变相雇佣关系并被追缴社保的风险。",
      ],
      sections: [
        {
          id: "porownanie",
          eyebrow: "01 / 对比",
          title: "两种形式的实际差异",
          blocks: [
            {
              kind: "table",
              caption: "2026 年波兰 IT 行业：B2B 与雇佣合同对比",
              head: ["对比维度", "雇佣合同（UoP）", "B2B 合同"],
              rows: [
                ["雇主成本", "约税前工资的 1.2 倍加福利", "发票金额（净额 + 可抵扣增值税）"],
                ["带薪假期", "26 天带薪假加病假", "无，除非合同中另有约定"],
                ["通知期", "工龄较长时最长 3 个月", "通常为 1 个月，由合同约定"],
                ["候选人偏好", "初级、部分中级及需要 on-call 的岗位", "大多数高级工程师和合同工"],
                ["知识产权归属", "默认由法律规定", "需在合同中明确约定"],
                ["扩张灵活性", "较低", "较高"],
              ],
            },
          ],
        },
        {
          id: "koszt",
          eyebrow: "02 / 预算",
          title: "如何计算真实雇佣成本",
          blocks: [
            {
              kind: "list",
              items: [
                "UoP：将税前工资乘以约 1.2 得到雇主成本，再叠加 26 天带薪假、病假和通知期成本。",
                "B2B：发票金额即为成本，但通常比对应的雇佣税前工资高 15%-25%，候选人会把缺失的假期和福利计入报价。",
                "福利：私人医疗、运动卡和培训预算每人每月合计约 400-900 PLN,在雇佣合同下已是标配，而非差异化优势。",
                "招聘本身：因职位空缺导致的路线图延迟成本，通常高于任何猎头费用，在为压缩招聘成本而精简流程前，先算清这笔账。",
              ],
            },
          ],
        },
        {
          id: "ryzyka",
          eyebrow: "03 / 风险",
          title: "B2B 可能存在风险的地方",
          blocks: [
            {
              kind: "p",
              text: "当 B2B 合作带有雇佣关系的特征时，固定工时、持续受支配、缺乏自主性，以及多年只为一个客户工作，风险就会出现。在波兰，这可能导致合作形式被重新认定，并被追缴社保费用。实践中的基本合规要求包括：真实的执行自主权、明确的交付成果范围、清晰的知识产权转让条款，以及规范处理的保密和 GDPR 条款。",
            },
          ],
        },
        {
          id: "eor",
          eyebrow: "04 / 无需设立公司",
          title: "在波兰无需自建公司也能招聘",
          blocks: [
            {
              kind: "list",
              items: [
                "Employer of Record：由第三方以雇佣合同形式代为雇佣，通常在税前工资基础上加收 8%-15%。启动最快、合规性最高，但单位成本也更高。",
                "直接签订 B2B 合同：即使没有波兰实体公司也可以进行，尤其常见于高级工程师；需要签订严谨的合同并了解上述风险。",
                "自建公司：团队规模超过约 10 人时通常比 EOR 更划算，但需要会计、人事支持，且需要数周时间才能启动。",
              ],
            },
            {
              kind: "p",
              text: "实践中，我们通常建议在招募最初三到五人时通过 EOR 或 B2B 方式启动，待波兰团队成为组织中长期稳定的一部分后，再过渡到自建实体。",
            },
          ],
        },
        {
          id: "wybor",
          eyebrow: "05 / 决策",
          title: "不同岗位该选哪种形式",
          blocks: [
            {
              kind: "cards",
              items: [
                { title: "初级和中级", meta: "雇佣合同", body: "稳定性和导师制在这里是真正的吸引力，雇佣合同能提高候选人的回复率。" },
                { title: "高级和架构师", meta: "B2B（雇佣合同为可选项）", body: "波兰大多数高级工程师以 B2B 方式工作；不提供这一选项会使候选人池缩小数十个百分点。" },
                { title: "需要 on-call 的岗位", meta: "雇佣合同", body: "值班和运维责任在雇佣关系下比按成果计费的合同更容易安排。" },
                { title: "有明确终点的项目", meta: "B2B", body: "迁移、审计、系统上线，能够干净利落地结束合作，是这类项目最大的价值所在。" },
              ],
            },
          ],
        },
      ],
      faqTitle: "常见问题",
      faq: [
        {
          q: "境外公司能否在没有波兰实体的情况下招聘程序员？",
          a: "可以，通常通过 Employer of Record（雇佣合同）或与候选人的个体经营实体直接签订 B2B 合同来实现。",
        },
        {
          q: "B2B 费率比雇佣税前工资高多少？",
          a: "通常高 15%-25%,因为候选人会把缺失的带薪假期、社保缴纳和福利计入报价。",
        },
        {
          q: "只提供雇佣合同会不会排除高级候选人？",
          a: "不会完全排除，但会缩小候选人池。如果只能提供雇佣合同，可以通过福利、培训预算和可预期的工作模式来弥补差距。",
        },
      ],
      cta: ctaZh,
      methodology:
        "本文为一般性信息，基于 Positive Recruitment 在 2025-2026 年于波兰开展的招聘流程整理而成，不构成法律或税务建议。",
    },
  },
];

ARTICLES.push(
  employerOfRecordPolska,
  itOutsourcingNearshoringPolska,
  headhuntingItDirectSearch,
  rekrutacjaItKrakow,
  rekrutacjaItWarszawa,
  rekrutacjaItCee,
  kosztRekrutacjiItPolska,
  aiWRekrutacjiIt
);

export const getArticle = (slug: string) => ARTICLES.find((a) => a.slug === slug);
